import React, {memo, useMemo} from 'react';

import {InboxModel} from '../../../../../libs/shared/core/src/models/api/inboxModel';
import {WorkspaceDisplaySectionTrackingTypesEnum} from '../../../../../libs/shared/core/src/models/tracking/contexts/workspaceTracking';
import {InboxesPanelId} from '../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {useAppSelector} from '../../../../helpers/appReduxHelpers';
import {buildSharedInboxCounterKey} from '../../../../helpers/counterHelpers';
import {buildTeamInboxPath} from '../../../../helpers/modelHelpers';
import {CountersVisibilityModesEnum} from '../../../../models/api/preferencesModel';
import {
  CellsRefinementOptionsEnum,
  RefinementOptionModel,
} from '../../../../models/api/refinementOptionModel';
import {InboxesFiltersEnum} from '../../../../models/url/models/inboxesUrlModel';
import {makeGetTeamInboxSelectedFilter} from '../../../../store/data/workspace/visualWorkspace/visualWorkspaceSelectors';
import {
  WorkspaceDragItem,
  WorkspaceDragSourceTypesEnum,
  WorkspaceItemDropHandler,
} from '../dnd/workspaceDragDropTypes';
import {WorkspaceInboxItemContainer} from '../workspaceInboxItemContainer';

/*
 * Props.
 */

interface TeamWorkspaceInboxItemProps {
  panelId: InboxesPanelId;
  inbox: InboxModel;
  isLastItem: boolean;
  isDisabled: boolean;
  countersVisibility: CountersVisibilityModesEnum;
  shouldAlphabetizeWorkspace: boolean;
  hasEditPermission: boolean;
  isNotDraggable?: boolean;
  onWorkspaceItemDrop: WorkspaceItemDropHandler;
  selectedRefinement: RefinementOptionModel | undefined;
  shouldUseOpenFilterForTeams: boolean;
}

/*
 * Component.
 */

// WorkspaceInboxItemContainer is a heavy component with many selectors, so it pays off to memoize this for
// users with many team inboxes.
export const TeamWorkspaceInboxItem = memo<TeamWorkspaceInboxItemProps>((props) => {
  const {
    panelId,
    inbox,
    isDisabled,
    isLastItem,
    countersVisibility,
    shouldAlphabetizeWorkspace,
    hasEditPermission,
    isNotDraggable,
    onWorkspaceItemDrop,
    selectedRefinement,
    shouldUseOpenFilterForTeams,
  } = props;

  const getSelectedFilter = useMemo(() => makeGetTeamInboxSelectedFilter<void>(() => inbox.id), [inbox.id]);
  const selectedFilter = useAppSelector(getSelectedFilter);

  const workspaceDragItem: WorkspaceDragItem = useMemo(
    () => ({
      type: WorkspaceDragSourceTypesEnum.TEAM_INBOX,
      namespace: inbox.namespace,
      id: inbox.id,
      isLast: isLastItem,
    }),
    [inbox.namespace, inbox.id, isLastItem],
  );

  return (
    <WorkspaceInboxItemContainer
      shouldAlphabetizeWorkspace={shouldAlphabetizeWorkspace}
      canBeHidden
      shouldHideMoreMenu={false}
      trackingSection={WorkspaceDisplaySectionTrackingTypesEnum.TEAM_INBOX}
      panelId={panelId}
      inbox={inbox}
      to={buildTeamInboxPath(
        inbox.id,
        !shouldUseOpenFilterForTeams && selectedRefinement?.type === CellsRefinementOptionsEnum.ASSIGNEE
          ? InboxesFiltersEnum.ASSIGNED
          : selectedFilter,
      )}
      counterKey={buildSharedInboxCounterKey(inbox.id, countersVisibility)}
      countersVisibility={countersVisibility}
      indentLevel={1}
      isDisabled={isDisabled}
      onWorkspaceItemDrop={onWorkspaceItemDrop}
      workspaceDragItem={isNotDraggable ? undefined : workspaceDragItem}
      hasEditPermission={hasEditPermission}
      shouldShowRefinedResultsIndicator={false}
      selectedRefinement={selectedRefinement}
    />
  );
});
